import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  ClipboardX,
  Clock3,
  EllipsisVertical,
  FileQuestion,
  FileText,
  FolderOpen,
  LineChart,
  List,
  Pencil,
  Plus,
  Star,
  Sparkles,
  Trash2,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import {
  type AssignmentItem,
  type StudentGroupItem,
  type StudentSubmissionItem,
  useCreateMultipleCourseAcquiredMarksMutation,
  useCreateStudentGroupMutation,
  useCreateStudentSubmissionMutation,
  useDeleteAssignmentMutation,
  useDeleteStudentGroupMutation,
  useCreateStudentGradeMutation,
  useLazyGetGradeStudentAssignmentQuery,
  useGetAssignmentDetailQuery,
  useGetCourseAcquiredMarksQuery,
  useGetEnrolledStudentsQuery,
  useGetStudentGroupsQuery,
  useGetAssignmentsQuery,
  useGetCourseSectionsQuery,
  useGetStudentSubmissionsQuery,
  usePatchStudentGroupMutation,
  usePatchStudentGradeMutation,
  useUpdateMultipleCourseAcquiredMarksMutation,
} from '@/state/course';
import { skipToken } from '@reduxjs/toolkit/query';
import { format, isValid } from 'date-fns';
import CourseAssignmentCreateDrawer from './course-assignment-create-drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useGetCurrentUserQuery } from '@/state/user';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RichTextEditor } from '@/components/rich-text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import CourseAssignmentSubmissionSheet from './course-assignment-submission-sheet';
import CourseAssignmentGradeDialog from './course-assignment-grade-dialog';
import CourseAssignmentAnswerSheet from './course-assignment-answer-sheet';
import CourseAssignmentTeamDialog from './course-assignment-team-dialog';
import CourseAssignmentSkillsAssessmentSheet from './course-assignment-skills-assessment-sheet';
import { useClientPermissions } from '@/hooks/use-client-permissions';

interface CourseAssignmentsContentProps {
  canEdit: boolean;
  tenant: string;
  courseId: number;
  courseStructureId: number;
  courseSectionId?: number | null;
  sectionName?: string | null;
  onNavigateToSectionAssignments?: (sectionId: number) => void;
  emptyText?: string;
  addLabel?: string;
}

interface AssignmentGroupItem {
  key: string;
  title: string;
  count: number;
  sectionId?: number;
}

interface TeamUserOption {
  id: string;
  name: string;
  email: string;
}

type TeamDialogMode = 'create' | 'edit';

const SKILL_LEVEL_LABELS: Record<number, string> = {
  1: 'Supplementary',
  2: 'Beneficial',
  3: 'Significant',
  4: 'Crucial',
  5: 'Essential',
};

const buildTextNode = (text: string) => ({
  type: 'text',
  text,
  format: 0,
  detail: 0,
  mode: 'normal',
  style: '',
  version: 1,
});

const buildParagraphNode = (text: string) => ({
  type: 'paragraph',
  children: [buildTextNode(text)],
  format: '',
  indent: 0,
  direction: 'ltr',
  version: 1,
});

const buildHeadingNode = (text: string) => ({
  type: 'heading',
  tag: 'h3',
  children: [{ ...buildTextNode(text), format: 1 }],
  format: '',
  indent: 0,
  direction: 'ltr',
  version: 1,
});

const buildBulletListNode = (items: string[]) => ({
  type: 'list',
  listType: 'bullet',
  tag: 'ul',
  start: 1,
  children: items.map((item, index) => ({
    type: 'listitem',
    value: index + 1,
    children: [buildTextNode(item)],
    format: '',
    indent: 0,
    direction: 'ltr',
    version: 1,
  })),
  format: '',
  indent: 0,
  direction: 'ltr',
  version: 1,
});

const getLexicalChildren = (value: unknown): unknown[] => {
  if (!value || typeof value !== 'object') return [];
  const root = (value as { root?: { children?: unknown[] } }).root;
  return Array.isArray(root?.children) ? root.children : [];
};

const buildAIFeedbackLexical = (ai: {
  summary?: string;
  strengths?: string[];
  weaknesses?: string[];
  feedback?: unknown;
  summary_lexical?: unknown;
  strengths_lexical?: unknown;
  weaknesses_lexical?: unknown;
  suggested_grade?: number;
}) => {
  const summaryNodes = getLexicalChildren(ai.summary_lexical);
  const strengthsNodes = getLexicalChildren(ai.strengths_lexical);
  const weaknessesNodes = getLexicalChildren(ai.weaknesses_lexical);
  const feedbackNodes = getLexicalChildren(ai.feedback);

  const children: unknown[] = [];
  if (typeof ai.suggested_grade === 'number') {
    children.push(buildHeadingNode('Suggested grade'));
    children.push(buildParagraphNode(String(ai.suggested_grade)));
  }
  children.push(buildHeadingNode('Summary'));
  if (summaryNodes.length) {
    children.push(...summaryNodes);
  } else if (ai.summary) {
    children.push(buildParagraphNode(ai.summary));
  }

  children.push(buildHeadingNode('Strengths'));
  if (strengthsNodes.length) {
    children.push(...strengthsNodes);
  } else if (ai.strengths?.length) {
    children.push(buildBulletListNode(ai.strengths));
  }

  children.push(buildHeadingNode('Weaknesses'));
  if (weaknessesNodes.length) {
    children.push(...weaknessesNodes);
  } else if (ai.weaknesses?.length) {
    children.push(buildBulletListNode(ai.weaknesses));
  }

  children.push(buildHeadingNode('Detailed feedback'));
  if (feedbackNodes.length) {
    children.push(...feedbackNodes);
  } else if (ai.summary) {
    children.push(buildParagraphNode(ai.summary));
  }

  return JSON.stringify({
    root: {
      children,
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  });
};

const formatAssignmentDeadline = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d yyyy, HH:mm');
};

const formatSimpleDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

const isPastAssignmentDeadline = (value?: string | null) => {
  if (!value) return false;
  const date = new Date(value);
  if (!isValid(date)) return false;
  return date.getTime() < Date.now();
};

const isLateSubmission = (submissionDate?: string | null, deadline?: string | null) => {
  if (!submissionDate || !deadline) return false;
  const submittedAt = new Date(submissionDate);
  const deadlineAt = new Date(deadline);
  if (!isValid(submittedAt) || !isValid(deadlineAt)) return false;
  return submittedAt.getTime() > deadlineAt.getTime();
};

const getGroupSubmission = (group: StudentGroupItem, submissionType = 1) =>
  group.submission?.find((item) => item.submission_type === submissionType) || null;

const getInitials = (firstName?: string, lastName?: string) =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim() || 'U';

const hasMeaningfulRichText = (value?: string | null) => {
  if (!value) return false;
  try {
    const parsed = JSON.parse(value);
    const stack = [parsed?.root];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (typeof node.text === 'string' && node.text.trim().length > 0) {
        return true;
      }
      if (Array.isArray(node.children)) {
        stack.push(...node.children);
      }
    }
    return false;
  } catch {
    return value.trim().length > 0;
  }
};

const CourseAssignmentsContent = ({
  canEdit,
  tenant,
  courseId,
  courseStructureId,
  courseSectionId = null,
  sectionName,
  onNavigateToSectionAssignments,
  emptyText = 'No assignments yet.',
  addLabel = 'Add assignment',
}: CourseAssignmentsContentProps) => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<AssignmentItem | null>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<AssignmentItem | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [assignmentDetailTab, setAssignmentDetailTab] = useState('submissions');
  const [skillView, setSkillView] = useState<'chart' | 'list'>('chart');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const [isGradeDialogOpen, setIsGradeDialogOpen] = useState(false);
  const [gradeFeedbackDraft, setGradeFeedbackDraft] = useState('');
  const [gradeScoreDraft, setGradeScoreDraft] = useState('');
  const [gradeStudentIds, setGradeStudentIds] = useState<string[]>([]);
  const [activeGradingSubmission, setActiveGradingSubmission] = useState<StudentSubmissionItem | null>(
    null
  );
  const [isAnswerDrawerOpen, setIsAnswerDrawerOpen] = useState(false);
  const [answerDraft, setAnswerDraft] = useState('');
  const [answerAttachment, setAnswerAttachment] = useState<File | null>(null);
  const [isTeamAnswerFormOpen, setIsTeamAnswerFormOpen] = useState(false);
  const [teamAnswerDraft, setTeamAnswerDraft] = useState('');
  const [teamAnswerAttachment, setTeamAnswerAttachment] = useState<File | null>(null);
  const [isReflectiveDrawerOpen, setIsReflectiveDrawerOpen] = useState(false);
  const [isSkillAssessmentOpen, setIsSkillAssessmentOpen] = useState(false);
  const [reflectiveDraft, setReflectiveDraft] = useState('');
  const [reflectiveAttachment, setReflectiveAttachment] = useState<File | null>(null);
  const [selectedReflectiveSubmissionId, setSelectedReflectiveSubmissionId] = useState<number | null>(
    null
  );
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState('');
  const [selectedTeamUserIds, setSelectedTeamUserIds] = useState<string[]>([]);
  const [teamDialogMode, setTeamDialogMode] = useState<TeamDialogMode>('create');
  const [teamDialogGroupId, setTeamDialogGroupId] = useState<number | null>(null);
  const [selectedTeamGroupId, setSelectedTeamGroupId] = useState<number | null>(null);
  const [activeMemberPopoverKey, setActiveMemberPopoverKey] = useState<string | null>(null);
  const [studentGroupToDelete, setStudentGroupToDelete] = useState<number | null>(null);
  const [leaderBlockedLeaveGroup, setLeaderBlockedLeaveGroup] = useState<number | null>(null);
  const gradeFeedbackGetterRef = useRef<(() => string) | null>(null);
  const answerGetterRef = useRef<(() => string) | null>(null);
  const teamAnswerGetterRef = useRef<(() => string) | null>(null);
  const reflectiveGetterRef = useRef<(() => string) | null>(null);
  const [deleteAssignment, { isLoading: isDeletingAssignment }] = useDeleteAssignmentMutation();
  const [createStudentGroup, { isLoading: isCreatingStudentGroup }] = useCreateStudentGroupMutation();
  const [patchStudentGroup, { isLoading: isLeavingStudentGroup }] = usePatchStudentGroupMutation();
  const [deleteStudentGroup, { isLoading: isDeletingStudentGroup }] = useDeleteStudentGroupMutation();
  const [createMultipleCourseAcquiredMarks, { isLoading: isSavingCourseAcquiredMarks }] =
    useCreateMultipleCourseAcquiredMarksMutation();
  const [updateMultipleCourseAcquiredMarks, { isLoading: isUpdatingCourseAcquiredMarks }] =
    useUpdateMultipleCourseAcquiredMarksMutation();
  const [createStudentSubmission, { isLoading: isCreatingStudentSubmission }] =
    useCreateStudentSubmissionMutation();
  const [createStudentGrade, { isLoading: isCreatingStudentGrade }] = useCreateStudentGradeMutation();
  const [patchStudentGrade, { isLoading: isPatchingStudentGrade }] = usePatchStudentGradeMutation();
  const [getGradeStudentAssignment, { isFetching: isFetchingAIFeedback }] =
    useLazyGetGradeStudentAssignmentQuery();
  const { hasAIModule } = useClientPermissions();
  const [activeAssignmentView, setActiveAssignmentView] = useState<'groups' | 'list'>(
    courseSectionId ? 'list' : 'groups'
  );
  const [selectedGroupSectionId, setSelectedGroupSectionId] = useState<number | null>(
    courseSectionId ?? null
  );
  const effectiveView = courseSectionId ? 'list' : activeAssignmentView;
  const effectiveSelectedGroupSectionId = courseSectionId ?? selectedGroupSectionId;

  const { data: assignments = [] } = useGetAssignmentsQuery(
    tenant && courseId
      ? {
          tenant,
          courseId,
          ...(effectiveView === 'list'
            ? { courseSectionId: effectiveSelectedGroupSectionId }
            : {}),
        }
      : skipToken
  );
  const { data: courseSections = [] } = useGetCourseSectionsQuery(
    tenant && courseId ? { tenant, courseId } : skipToken
  );
  const { data: selectedAssignmentDetail } = useGetAssignmentDetailQuery(
    tenant && selectedAssignmentId
      ? { tenant, assignmentId: selectedAssignmentId }
      : skipToken
  );
  const selectedAssignmentFromAll = useMemo(
    () => assignments.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [assignments, selectedAssignmentId]
  );
  const isGroupAssignmentForQueries =
    (selectedAssignmentDetail?.assignment_type || selectedAssignmentFromAll?.assignment_type) ===
    'GROUP';
  const { data: selectedAssignmentSubmissions = [] } = useGetStudentSubmissionsQuery(
    tenant && selectedAssignmentId && !isGroupAssignmentForQueries
      ? { tenant, assignmentId: selectedAssignmentId }
      : skipToken
  );
  const { data: enrolledStudents = [] } = useGetEnrolledStudentsQuery(
    tenant && courseId ? { tenant, courseId } : skipToken
  );
  const { data: currentUser } = useGetCurrentUserQuery(tenant ? { tenant } : skipToken);
  const { data: selectedAssignmentGroups = [], isLoading: isAssignmentGroupsLoading } =
    useGetStudentGroupsQuery(
    tenant &&
      selectedAssignmentId &&
      isGroupAssignmentForQueries &&
      (canEdit || Boolean(currentUser?.id))
      ? {
          tenant,
          assignmentId: selectedAssignmentId,
          ...(canEdit ? {} : { userId: currentUser?.id }),
        }
      : skipToken
    );

  const assignmentGroups = useMemo<AssignmentGroupItem[]>(() => {
    const courseAssignmentCount = assignments.filter((item) => item.course_section === null).length;
    const sectionGroups = courseSections
      .map((section) => ({
        sectionId: section.id,
        sectionName: section.section_name || `Section ${section.id}`,
        count: assignments.filter((item) => item.course_section === section.id).length,
      }))
      .filter((item) => item.count > 0);

    return [
      {
        key: 'course',
        title: 'Course assignments',
        count: courseAssignmentCount,
      },
      ...sectionGroups.map((item) => ({
        key: String(item.sectionId),
        title: item.sectionName,
        count: item.count,
        sectionId: item.sectionId,
      })),
    ];
  }, [assignments, courseSections]);

  const displayedAssignments = useMemo(() => {
    if (effectiveView !== 'list') return [];
    if (effectiveSelectedGroupSectionId === null) {
      return assignments.filter((assignment) => assignment.course_section === null);
    }
    return assignments.filter(
      (assignment) => assignment.course_section === effectiveSelectedGroupSectionId
    );
  }, [effectiveView, assignments, effectiveSelectedGroupSectionId]);

  const selectedListLabel = useMemo(() => {
    if (courseSectionId) return sectionName || 'Section assignments';
    if (effectiveSelectedGroupSectionId === null) return 'Course assignments';
    return (
      courseSections.find((section) => section.id === effectiveSelectedGroupSectionId)?.section_name ||
      `Section ${effectiveSelectedGroupSectionId}`
    );
  }, [courseSectionId, sectionName, effectiveSelectedGroupSectionId, courseSections]);
  const selectedAssignmentFromList = useMemo(
    () => displayedAssignments.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [displayedAssignments, selectedAssignmentId]
  );
  const selectedAssignmentData = selectedAssignmentDetail || selectedAssignmentFromList;
  const selectedAssignmentName = selectedAssignmentData?.assignment_name || 'Assignment';
  const selectedAssignmentType = selectedAssignmentData?.assignment_type;
  const isGroupAssignment = selectedAssignmentType === 'GROUP';
  const selectedAssignmentDeadline = selectedAssignmentData?.submission_deadline;
  const selectedAssignmentMarkingScheme = selectedAssignmentData?.marking_scheme;
  const { data: selectedAssignmentAcquiredMarks = [] } = useGetCourseAcquiredMarksQuery(
    tenant && selectedAssignmentMarkingScheme?.id
      ? {
          tenant,
          markingScheme: selectedAssignmentMarkingScheme.id,
          courseId,
        }
      : skipToken
  );
  const selectedAssignmentBrief = selectedAssignmentData?.assignment_brief || '';
  const selectedAssignmentAttachments = selectedAssignmentData?.assignment_repo || [];
  const selectedAssignmentSkills = useMemo(
    () => selectedAssignmentData?.assignments_skills || [],
    [selectedAssignmentData?.assignments_skills]
  );
  const skillAssessmentSkills = useMemo(
    () =>
      selectedAssignmentSkills.map((skill) => ({
        id: String(skill.skills ?? skill.id ?? ''),
        name: skill.skill_name || 'Skill',
      })),
    [selectedAssignmentSkills]
  );
  const selectedAssignmentSkillsChartData = useMemo(
    () =>
      selectedAssignmentSkills.map((skill) => ({
        id: skill.id,
        skill: skill.skill_name || 'Skill',
        level: Math.max(0, Math.min(5, Number(skill.required_level) || 0)),
      })),
    [selectedAssignmentSkills]
  );
  const selectedSubmission = useMemo(
    () =>
      selectedAssignmentSubmissions.find((submission) => submission.id === selectedSubmissionId) ||
      null,
    [selectedAssignmentSubmissions, selectedSubmissionId]
  );
  const selectedSubmissionOwnFeedback = useMemo(
    () =>
      selectedSubmission?.student_submission?.find(
        (feedback) => feedback.feedback_provided_by?.id === currentUser?.id
      ) || null,
    [selectedSubmission, currentUser?.id]
  );
  const currentUserHasTeam = useMemo(() => {
    if (!currentUser?.id) return false;
    return selectedAssignmentGroups.some((group) =>
      group.users?.some((user) => user.id === currentUser.id)
    );
  }, [selectedAssignmentGroups, currentUser?.id]);
  const skillAssessmentUsers = useMemo(() => {
    if (canEdit) {
      return enrolledStudents.map((entry) => ({
        id: entry.student.id,
        name: `${entry.student.first_name} ${entry.student.last_name}`.trim() || entry.student.email,
        email: entry.student.email,
        photoUrl: entry.student.photo_url,
      }));
    }
    const map = new Map<string, { id: string; name: string; email: string; photoUrl?: string | null }>();
    selectedAssignmentGroups.forEach((group) => {
      (group.users || []).forEach((user) => {
        if (!user?.id) return;
        const name = user.name || user.email || 'User';
        map.set(user.id, {
          id: user.id,
          name,
          email: user.email || '',
          photoUrl: user.photo_url,
        });
      });
    });
    return Array.from(map.values());
  }, [canEdit, enrolledStudents, selectedAssignmentGroups]);
  const skillAssessmentListingUsers = useMemo(() => {
    if (canEdit) {
      return enrolledStudents.map((entry) => ({
        id: entry.student.id,
        name: `${entry.student.first_name} ${entry.student.last_name}`.trim() || entry.student.email,
        email: entry.student.email,
        photoUrl: entry.student.photo_url,
      }));
    }
    if (currentUser?.id) {
      return [
        {
          id: currentUser.id,
          name:
            `${currentUser.profile?.first_name ?? ''} ${currentUser.profile?.last_name ?? ''}`.trim() ||
            currentUser.email ||
            'You',
          email: currentUser.email || '',
          photoUrl: currentUser.profile?.photo_url ?? null,
        },
      ];
    }
    return [];
  }, [canEdit, enrolledStudents, currentUser?.id, currentUser?.email, currentUser?.profile?.first_name, currentUser?.profile?.last_name, currentUser?.profile?.photo_url]);
  const teamUserOptions = useMemo<TeamUserOption[]>(
    () =>
      enrolledStudents.map((entry) => ({
        id: entry.student.id,
        name: `${entry.student.first_name} ${entry.student.last_name}`.trim(),
        email: entry.student.email,
      })),
    [enrolledStudents]
  );
  const selectedTeamUsers = useMemo(
    () => {
      return selectedTeamUserIds
        .map((id) => {
          const fromList = teamUserOptions.find((option) => option.id === id);
          if (fromList) return fromList;
          if (id === currentUser?.id) {
            return {
              id,
              name: `${currentUser.profile?.first_name || ''} ${currentUser.profile?.last_name || ''}`.trim() || 'You',
              email: currentUser.email || '',
            };
          }
          return null;
        })
        .filter((item): item is TeamUserOption => Boolean(item));
    },
    [selectedTeamUserIds, teamUserOptions, currentUser]
  );
  const filteredTeamUserOptions = useMemo(() => {
    return teamUserOptions.filter((option) => {
      if (selectedTeamUserIds.includes(option.id)) return false;
      return true;
    });
  }, [teamUserOptions, selectedTeamUserIds]);
  const selectedTeamGroup = useMemo(
    () => selectedAssignmentGroups.find((group) => group.id === selectedTeamGroupId) || null,
    [selectedAssignmentGroups, selectedTeamGroupId]
  );
  const selectedTeamSubmission = useMemo(
    () => (selectedTeamGroup ? getGroupSubmission(selectedTeamGroup, 1) : null),
    [selectedTeamGroup]
  );
  const selectedTeamReflectiveSubmissions = useMemo(
    () =>
      selectedTeamGroup?.submission?.filter((item) => item.submission_type === 2) || [],
    [selectedTeamGroup]
  );
  const selectedTeamOwnReflectiveSubmission = useMemo(
    () =>
      selectedTeamReflectiveSubmissions.find(
        (item) => item.submitted_by?.id === currentUser?.id
      ) || null,
    [selectedTeamReflectiveSubmissions, currentUser?.id]
  );
  const selectedReflectiveSubmission = useMemo(
    () =>
      selectedTeamReflectiveSubmissions.find((item) => item.id === selectedReflectiveSubmissionId) ||
      null,
    [selectedTeamReflectiveSubmissions, selectedReflectiveSubmissionId]
  );
  const selectedTeamSubmissionOwnFeedback = useMemo(
    () =>
      selectedTeamSubmission?.student_submission?.find(
        (feedback) => feedback.feedback_provided_by?.id === currentUser?.id
      ) || null,
    [selectedTeamSubmission, currentUser?.id]
  );
  const courseAcquiredMarksByStudentId = useMemo(() => {
    const map = new Map<string, number>();
    selectedAssignmentAcquiredMarks.forEach((item) => {
      map.set(item.student, Number(item.marks) || 0);
    });
    return map;
  }, [selectedAssignmentAcquiredMarks]);
  const selectedTeamScore = useMemo(() => {
    if (!selectedTeamGroup?.users?.length) return null;
    const firstUserId = selectedTeamGroup.users[0]?.id;
    if (!firstUserId) return null;
    const marks = courseAcquiredMarksByStudentId.get(firstUserId);
    return marks === undefined ? null : marks;
  }, [selectedTeamGroup, courseAcquiredMarksByStudentId]);
  const enrollmentByStudentId = useMemo(() => {
    const map = new Map<string, number>();
    enrolledStudents.forEach((entry) => {
      map.set(entry.student.id, entry.id);
    });
    return map;
  }, [enrolledStudents]);
  const effectiveAssignmentDetailTab = useMemo(() => {
    if (isGroupAssignment) {
      if (canEdit) {
        return assignmentDetailTab === 'teams' ? 'submissions' : assignmentDetailTab;
      }
      if (assignmentDetailTab === 'submissions') return 'teams';
      return assignmentDetailTab;
    }
    return !canEdit && assignmentDetailTab === 'submissions' ? 'details' : assignmentDetailTab;
  }, [assignmentDetailTab, canEdit, isGroupAssignment]);
  const currentUserSubmission = useMemo(
    () =>
      selectedAssignmentSubmissions.find((submission) => submission.submitted_by.id === currentUser?.id) ||
      null,
    [selectedAssignmentSubmissions, currentUser?.id]
  );
  const isSubmissionReviewed = (submission: StudentSubmissionItem) =>
    canEdit
      ? Boolean(getSubmissionOwnFeedback(submission))
      : (submission.student_submission?.length || 0) > 0;

  const getSubmissionOwnFeedback = (submission: StudentSubmissionItem) =>
    submission.student_submission?.find(
      (feedback) => feedback.feedback_provided_by?.id === currentUser?.id
    ) || null;

  const handleOpenGradeDialog = (
    submission: StudentSubmissionItem,
    studentIds: string[] = [submission.submitted_by.id],
    syncSubmissionSheet = true
  ) => {
    const ownFeedback = getSubmissionOwnFeedback(submission);
    const firstScore = courseAcquiredMarksByStudentId.get(studentIds[0]);
    if (syncSubmissionSheet) {
      setSelectedSubmissionId(submission.id);
    }
    setGradeFeedbackDraft(ownFeedback?.feedback || '');
    setGradeScoreDraft(firstScore !== undefined ? String(firstScore) : '');
    setGradeStudentIds(studentIds);
    setActiveGradingSubmission(submission);
    setIsGradeDialogOpen(true);
  };

  const handleOpenAIFeedback = async (
    submission: StudentSubmissionItem,
    studentIds: string[] = [submission.submitted_by.id],
    syncSubmissionSheet = true
  ) => {
    if (!tenant) return;
    try {
      const ai = await getGradeStudentAssignment({
        tenant,
        submissionId: submission.id,
      }).unwrap();
      if (syncSubmissionSheet) {
        setSelectedSubmissionId(submission.id);
      }
      setGradeFeedbackDraft(buildAIFeedbackLexical(ai));
      if (typeof ai.suggested_grade === 'number') {
        setGradeScoreDraft(String(ai.suggested_grade));
      }
      setGradeStudentIds(studentIds);
      setActiveGradingSubmission(submission);
      setIsGradeDialogOpen(true);
    } catch {
      toast.error('Failed to generate AI feedback.');
    }
  };

  const handleSubmitGrade = async () => {
    const submissionToGrade = activeGradingSubmission || selectedSubmission;
    if (!tenant || !submissionToGrade) return;
    const shouldHandleScore = isGroupAssignment;
    const fullMarks = Number(selectedAssignmentMarkingScheme?.full_marks ?? 0);
    const markingSchemeId = selectedAssignmentMarkingScheme?.id;
    const parsedScore = Number(gradeScoreDraft);
    if (shouldHandleScore) {
      if (!markingSchemeId || Number.isNaN(parsedScore)) {
        toast.error('Score is required.');
        return;
      }
      if (parsedScore < 0 || parsedScore > fullMarks) {
        toast.error(`Score must be between 0 and ${fullMarks}.`);
        return;
      }
    }
    const serializedFeedback =
      gradeFeedbackGetterRef.current?.() ||
      gradeFeedbackDraft ||
      '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}';
    const ownFeedback = getSubmissionOwnFeedback(submissionToGrade);

    try {
      if (ownFeedback?.id) {
        await patchStudentGrade({
          tenant,
          gradeId: ownFeedback.id,
          data: {
            feedback: serializedFeedback,
          },
        }).unwrap();
        toast.success('Grade feedback updated successfully.');
      } else {
        await createStudentGrade({
          tenant,
          data: {
            feedback: serializedFeedback,
            student_submission: submissionToGrade.id,
          },
        }).unwrap();
        toast.success('Grade feedback submitted successfully.');
      }
      if (shouldHandleScore && markingSchemeId) {
        const marksPayload = gradeStudentIds
          .map((studentId) => {
            const studentEnroll = enrollmentByStudentId.get(studentId);
            if (!studentEnroll) return null;
            const existingMarksRecord = selectedAssignmentAcquiredMarks.find(
              (record) => record.student === studentId
            );
            return {
              marks: parsedScore,
              full_marks: fullMarks,
              grade: null,
              status: 3,
              marking_scheme: markingSchemeId,
              student: studentId,
              course: courseId,
              student_enroll: studentEnroll,
              ...(existingMarksRecord?.id ? { id: existingMarksRecord.id } : {}),
            };
          })
          .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
        if (marksPayload.length > 0) {
          const hasAllRecordIds = marksPayload.every((entry) => typeof entry.id === 'number');
          if (ownFeedback?.id && hasAllRecordIds) {
            await updateMultipleCourseAcquiredMarks({
              tenant,
              data: { students: marksPayload },
            }).unwrap();
          } else {
            await createMultipleCourseAcquiredMarks({
              tenant,
              data: { students: marksPayload },
            }).unwrap();
          }
        }
      }
      setIsGradeDialogOpen(false);
    } catch {
      toast.error('Failed to submit grade feedback.');
    }
  };

  const handleSubmitStudentAnswer = async () => {
    if (!tenant || !selectedAssignmentId) return;
    const serializedAnswer =
      answerGetterRef.current?.() ||
      answerDraft ||
      '{"root":{"children":[{"children":[],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}';
    const hasAnswer = hasMeaningfulRichText(serializedAnswer);
    if (!hasAnswer && !answerAttachment) {
      toast.error('Submission requires either an answer or an attachment.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('assignment_details', serializedAnswer);
      formData.append('assignment', String(selectedAssignmentId));
      formData.append('submission_type', '1');
      if (answerAttachment) {
        formData.append('assignment_file', answerAttachment);
      }
      await createStudentSubmission({
        tenant,
        data: formData,
      }).unwrap();
      toast.success('Assignment submitted successfully.');
      setIsAnswerDrawerOpen(false);
      setAnswerDraft('');
      setAnswerAttachment(null);
      answerGetterRef.current = null;
    } catch {
      toast.error('Failed to submit assignment.');
    }
  };

  const handleSubmitTeamAnswer = async () => {
    if (!tenant || !selectedAssignmentId || !selectedTeamGroup) return;
    const serializedAnswer =
      teamAnswerGetterRef.current?.() ||
      teamAnswerDraft ||
      '{"root":{"children":[{"children":[],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}';
    const hasAnswer = hasMeaningfulRichText(serializedAnswer);
    if (!hasAnswer && !teamAnswerAttachment) {
      toast.error('Submission requires either an answer or an attachment.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('assignment_details', serializedAnswer);
      formData.append('assignment', String(selectedAssignmentId));
      formData.append('submission_type', '1');
      formData.append('submission_group', String(selectedTeamGroup.id));
      if (teamAnswerAttachment) {
        formData.append('assignment_file', teamAnswerAttachment);
      }
      await createStudentSubmission({
        tenant,
        data: formData,
      }).unwrap();
      toast.success('Team answer submitted successfully.');
      setIsTeamAnswerFormOpen(false);
      setTeamAnswerDraft('');
      setTeamAnswerAttachment(null);
      teamAnswerGetterRef.current = null;
    } catch {
      toast.error('Failed to submit team answer.');
    }
  };

  const handleSubmitReflectiveReport = async () => {
    if (!tenant || !selectedAssignmentId || !selectedTeamGroup) return;
    const serializedReport =
      reflectiveGetterRef.current?.() ||
      reflectiveDraft ||
      '{"root":{"children":[{"children":[],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}';
    const hasReport = hasMeaningfulRichText(serializedReport);
    if (!hasReport && !reflectiveAttachment) {
      toast.error('Submission requires either an answer or an attachment.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('assignment_details', serializedReport);
      formData.append('assignment', String(selectedAssignmentId));
      formData.append('submission_type', '2');
      formData.append('submission_group', String(selectedTeamGroup.id));
      if (reflectiveAttachment) {
        formData.append('assignment_file', reflectiveAttachment);
      }
      await createStudentSubmission({
        tenant,
        data: formData,
      }).unwrap();
      toast.success('Self-reflective report submitted successfully.');
      setReflectiveDraft('');
      setReflectiveAttachment(null);
      reflectiveGetterRef.current = null;
      setSelectedReflectiveSubmissionId(null);
    } catch {
      toast.error('Failed to submit self-reflective report.');
    }
  };

  const openCreateTeamDialog = () => {
    if (selectedAssignmentType !== 'GROUP') return;
    setTeamDialogMode('create');
    setTeamDialogGroupId(null);
    setTeamName('');
    setSelectedTeamMemberOption('');
    if (!canEdit && currentUser?.id) {
      setSelectedTeamUserIds([currentUser.id]);
    } else {
      setSelectedTeamUserIds([]);
    }
    setIsCreateTeamDialogOpen(true);
  };

  const openEditTeamDialog = (groupId: number) => {
    const group = selectedAssignmentGroups.find((entry) => entry.id === groupId);
    if (!group) return;
    setTeamDialogMode('edit');
    setTeamDialogGroupId(group.id);
    setTeamName(group.group_name || '');
    setSelectedTeamMemberOption('');
    setSelectedTeamUserIds((group.users || []).map((user) => user.id));
    setIsCreateTeamDialogOpen(true);
  };

  const handleRemoveTeamMember = (userId: string) => {
    if (teamDialogMode === 'create' && !canEdit && currentUser?.id === userId) return;
    setSelectedTeamUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSubmitCreateTeam = async () => {
    if (!tenant || !selectedAssignmentId || !currentUser?.id) return;
    const cleanTeamName = teamName.trim();
    if (!cleanTeamName) {
      toast.error('Team name is required.');
      return;
    }
    if (selectedTeamUserIds.length === 0) {
      toast.error('Please select at least one team member.');
      return;
    }
    const payloadUsers =
      !canEdit && currentUser?.id
        ? Array.from(new Set([...selectedTeamUserIds, currentUser.id]))
        : selectedTeamUserIds;
    try {
      if (teamDialogMode === 'edit' && teamDialogGroupId) {
        const existingGroup = selectedAssignmentGroups.find((group) => group.id === teamDialogGroupId);
        await patchStudentGroup({
          tenant,
          groupId: teamDialogGroupId,
          data: {
            group_name: cleanTeamName,
            users: payloadUsers,
            group_leader: existingGroup?.group_leader?.id || currentUser.id,
          },
        }).unwrap();
        toast.success('Team updated successfully.');
      } else {
        await createStudentGroup({
          tenant,
          data: {
            group_name: cleanTeamName,
            assignment: selectedAssignmentId,
            users: payloadUsers,
            group_leader: currentUser.id,
          },
        }).unwrap();
        toast.success('Team created successfully.');
      }
      setIsCreateTeamDialogOpen(false);
      setTeamDialogGroupId(null);
      setTeamName('');
      setSelectedTeamMemberOption('');
      setSelectedTeamUserIds([]);
    } catch {
      toast.error(teamDialogMode === 'edit' ? 'Failed to update team.' : 'Failed to create team.');
    }
  };

  const handleLeaveTeam = async (groupId: number) => {
    if (!tenant || !currentUser?.id) return;
    const group = selectedAssignmentGroups.find((item) => item.id === groupId);
    if (!group) return;
    if (group.group_leader?.id === currentUser.id) {
      setLeaderBlockedLeaveGroup(groupId);
      return;
    }
    const usersAfterLeave = (group.users || [])
      .map((user) => user.id)
      .filter((userId) => userId !== currentUser.id);
    try {
      await patchStudentGroup({
        tenant,
        groupId,
        data: {
          users: usersAfterLeave,
        },
      }).unwrap();
      toast.success('You left the team.');
    } catch {
      toast.error('Failed to leave team.');
    }
  };

  const handleDeleteTeam = async () => {
    if (!tenant || !studentGroupToDelete) return;
    try {
      await deleteStudentGroup({
        tenant,
        groupId: studentGroupToDelete,
      }).unwrap();
      toast.success('Team deleted successfully.');
      setStudentGroupToDelete(null);
      setSelectedTeamGroupId(null);
    } catch {
      toast.error('Failed to delete team.');
    }
  };

  const handleChangeTeamLeader = async (groupId: number, leaderId: string) => {
    if (!tenant) return;
    const group = selectedAssignmentGroups.find((entry) => entry.id === groupId);
    if (!group) return;
    try {
      await patchStudentGroup({
        tenant,
        groupId,
        data: {
          group_name: group.group_name,
          users: group.users.map((user) => user.id),
          group_leader: leaderId,
        },
      }).unwrap();
      toast.success('Team leader updated successfully.');
    } catch {
      toast.error('Failed to update team leader.');
    }
  };

  const handleRemoveMemberFromGroup = async (groupId: number, userId: string) => {
    if (!tenant) return;
    const group = selectedAssignmentGroups.find((entry) => entry.id === groupId);
    if (!group) return;
    if (group.group_leader?.id === userId) {
      setLeaderBlockedLeaveGroup(groupId);
      return;
    }
    const nextUsers = group.users.filter((user) => user.id !== userId).map((user) => user.id);
    try {
      await patchStudentGroup({
        tenant,
        groupId,
        data: {
          group_name: group.group_name,
          users: nextUsers,
          group_leader: group.group_leader?.id,
        },
      }).unwrap();
      toast.success('Member removed successfully.');
      setActiveMemberPopoverKey(null);
    } catch {
      toast.error('Failed to remove member.');
    }
  };

  return (
    <div className="space-y-4">
      {selectedAssignmentId ? (
        <div className="space-y-4 rounded-xl border bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              onClick={() => setSelectedAssignmentId(null)}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to assignments
            </button>
            {canEdit ? (
              <div className="flex items-center gap-2">
                {selectedAssignmentType === 'GROUP' ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsSkillAssessmentOpen(true)}
                  >
                    <LineChart className="h-4 w-4" />
                    Skill assessment
                  </Button>
                ) : null}
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-3 text-sm text-primary transition-colors hover:bg-primary/15"
                  onClick={() => {
                    if (selectedAssignmentData) {
                      setEditingAssignment(selectedAssignmentData);
                      setIsDrawerOpen(true);
                    }
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-3 text-sm text-destructive transition-colors hover:bg-destructive/15"
                  onClick={() => {
                    if (selectedAssignmentData) setAssignmentToDelete(selectedAssignmentData);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            ) : selectedAssignmentType === 'GROUP' ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsSkillAssessmentOpen(true)}
                >
                  <LineChart className="h-4 w-4" />
                  Skill assessment
                </Button>
                {!isAssignmentGroupsLoading && !currentUserHasTeam ? (
                  <Button type="button" onClick={openCreateTeamDialog}>
                    <Plus className="h-4 w-4" />
                    Create team
                  </Button>
                ) : null}
              </div>
            ) : currentUserSubmission ? (
              <Button type="button" variant="outline" onClick={() => setSelectedSubmissionId(currentUserSubmission.id)}>
                View my answer
              </Button>
            ) : (
              <Button type="button" onClick={() => setIsAnswerDrawerOpen(true)}>
                Answer assignment
              </Button>
            )}
          </div>

          <div className="rounded-lg border bg-background/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xl font-semibold">{selectedAssignmentName}</p>
                <div className="mt-1 inline-flex items-center gap-3 text-sm text-muted-foreground">
                  <span
                    className={
                      isPastAssignmentDeadline(selectedAssignmentDeadline)
                        ? 'text-red-500'
                        : 'text-muted-foreground'
                    }
                  >
                    <Clock3 className="mr-1 inline h-4 w-4" />
                    {formatAssignmentDeadline(selectedAssignmentDeadline)}
                  </span>
                  <span>
                    {selectedAssignmentType === 'GROUP' ? 'Team assignment' : 'Individual assignment'}
                  </span>
                  {!canEdit ? (
                    <span
                      className={
                        currentUserSubmission && isSubmissionReviewed(currentUserSubmission)
                          ? 'rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300'
                          : 'rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300'
                      }
                    >
                      {currentUserSubmission && isSubmissionReviewed(currentUserSubmission)
                        ? 'Reviewed'
                        : 'Pending'}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 text-right">
                <div>
                  <p className="text-xs text-muted-foreground">Full marks</p>
                  <p className="text-xl font-semibold">
                    {selectedAssignmentMarkingScheme?.full_marks ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Weightage</p>
                  <p className="text-xl font-semibold">
                    {selectedAssignmentMarkingScheme?.course_percentage ?? 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Tabs value={effectiveAssignmentDetailTab} onValueChange={setAssignmentDetailTab}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <TabsList className="inline-flex h-auto w-auto justify-start gap-1 self-start">
                {isGroupAssignment ? (
                  canEdit ? (
                    <TabsTrigger value="submissions" className="!flex-none gap-1.5 px-2 sm:px-3">
                      <Users className="h-4 w-4" />
                      <span className="hidden sm:inline">Submissions</span>
                    </TabsTrigger>
                  ) : (
                    <TabsTrigger value="teams" className="!flex-none gap-1.5 px-2 sm:px-3">
                      <Users className="h-4 w-4" />
                      <span className="hidden sm:inline">My teams</span>
                    </TabsTrigger>
                  )
                ) : canEdit ? (
                  <TabsTrigger value="submissions" className="!flex-none gap-1.5 px-2 sm:px-3">
                    <Users className="h-4 w-4" />
                    <span className="hidden sm:inline">Submissions</span>
                  </TabsTrigger>
                ) : null}
                <TabsTrigger value="details" className="!flex-none gap-1.5 px-2 sm:px-3">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Details</span>
                </TabsTrigger>
                <TabsTrigger value="attachments" className="!flex-none gap-1.5 px-2 sm:px-3">
                  <FolderOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Attachments</span>
                </TabsTrigger>
                <TabsTrigger value="skills" className="!flex-none gap-1.5 px-2 sm:px-3">
                  <LineChart className="h-4 w-4" />
                  <span className="hidden sm:inline">Skills</span>
                </TabsTrigger>
              </TabsList>
              {canEdit && selectedAssignmentType === 'GROUP' ? (
                <Button type="button" variant="ghost" className="text-primary" onClick={openCreateTeamDialog}>
                  <Plus className="h-4 w-4" />
                  Create team
                </Button>
              ) : null}
            </div>

            {canEdit ? (
              <TabsContent value="submissions" className="mt-3">
                {isGroupAssignment ? (
                  selectedAssignmentGroups.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border">
                      <div className="min-w-[900px]">
                        <div className="grid grid-cols-12 border-b bg-muted/40 px-4 py-3 text-sm font-medium">
                          <p className="col-span-5">Teams</p>
                          <p className="col-span-2">Users</p>
                          <p className="col-span-2">Submission date</p>
                          <p className="col-span-2">Status</p>
                          <p className="col-span-1">Grade</p>
                        </div>
                        {selectedAssignmentGroups.map((group) => (
                          <div
                            key={group.id}
                            role="button"
                            tabIndex={0}
                            className="grid cursor-pointer grid-cols-12 items-center gap-2 border-b px-4 py-3 transition-colors hover:bg-accent/20 last:border-b-0"
                            onClick={() => {
                              setSelectedTeamGroupId(group.id);
                              setIsTeamAnswerFormOpen(false);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSelectedTeamGroupId(group.id);
                                setIsTeamAnswerFormOpen(false);
                              }
                            }}
                          >
                            {(() => {
                              const groupSubmission = getGroupSubmission(group);
                              const reviewed = groupSubmission
                                ? Boolean(getSubmissionOwnFeedback(groupSubmission))
                                : false;
                              const scoreValue = courseAcquiredMarksByStudentId.get(
                                group.users?.[0]?.id || ''
                              );
                              return (
                                <>
                            <p className="col-span-5 text-base font-medium">{group.group_name}</p>
                            <div className="col-span-2 flex items-center">
                              {group.users.slice(0, 4).map((user, index) => (
                                <Tooltip key={user.id}>
                                  <TooltipTrigger asChild>
                                    <Avatar
                                      className={`h-8 w-8 border-2 border-background ${index > 0 ? '-ml-2' : ''}`}
                                    >
                                      <AvatarImage src={user.photo_url || undefined} />
                                      <AvatarFallback>{getInitials(user.name, '')}</AvatarFallback>
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent sideOffset={6}>{user.name}</TooltipContent>
                                </Tooltip>
                              ))}
                            </div>
                            <p className="col-span-2 text-sm text-muted-foreground">
                              {groupSubmission
                                ? formatAssignmentDeadline(groupSubmission.submission_date)
                                : 'N/A'}
                            </p>
                            <p className="col-span-2">
                              {groupSubmission ? (
                                reviewed ? (
                                  <span className="rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300">
                                    Reviewed
                                  </span>
                                ) : (
                                  <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                                    Pending
                                  </span>
                                )
                              ) : (
                                <span className="text-sm text-muted-foreground">N/A</span>
                              )}
                            </p>
                            <p className="col-span-1 text-sm text-muted-foreground">
                              {groupSubmission && scoreValue !== undefined
                                ? `${scoreValue}/${selectedAssignmentMarkingScheme?.full_marks ?? 0}`
                                : 'N/A'}
                            </p>
                                </>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-10 text-center">
                      <ClipboardX className="mx-auto h-9 w-9 text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">No team data available.</p>
                    </div>
                  )
                ) : selectedAssignmentSubmissions.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border">
                    <div className="min-w-[760px]">
                      <div className="grid grid-cols-12 border-b bg-muted/40 px-4 py-3 text-sm font-medium">
                        <p className="col-span-6">Learners</p>
                        <p className="col-span-4">Submission date</p>
                        <p className="col-span-2">Status</p>
                      </div>
                      {selectedAssignmentSubmissions.map((submission) => (
                        <div
                          key={submission.id}
                          role="button"
                          tabIndex={0}
                          className="grid cursor-pointer grid-cols-12 items-center gap-2 border-b px-4 py-3 transition-colors hover:bg-accent/20 last:border-b-0"
                          onClick={() => setSelectedSubmissionId(submission.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedSubmissionId(submission.id);
                            }
                          }}
                        >
                          <div className="col-span-6 flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={submission.submitted_by.photo_url || undefined} />
                              <AvatarFallback>
                                {`${submission.submitted_by.first_name?.[0] || ''}${submission.submitted_by.last_name?.[0] || ''}`}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">
                                {submission.submitted_by.first_name} {submission.submitted_by.last_name}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {submission.submitted_by.email}
                              </p>
                            </div>
                          </div>
                          <p className="col-span-4 text-sm text-muted-foreground">
                            {formatAssignmentDeadline(submission.submission_date)}
                          </p>
                          <p className="col-span-2">
                            <span className="inline-flex flex-wrap items-center gap-1">
                              {isSubmissionReviewed(submission) ? (
                                <span className="rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300">
                                  Reviewed
                                </span>
                              ) : (
                                <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                                  Pending
                                </span>
                              )}
                              {isLateSubmission(
                                submission.submission_date,
                                selectedAssignmentDeadline
                              ) ? (
                                <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                                  Late submission
                                </span>
                              ) : null}
                            </span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-10 text-center">
                    <Users className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">No submissions yet.</p>
                  </div>
                )}
              </TabsContent>
            ) : null}

            {!canEdit && isGroupAssignment ? (
              <TabsContent value="teams" className="mt-3">
                {selectedAssignmentGroups.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
                    {selectedAssignmentGroups.map((group) => {
                      const groupSubmission = getGroupSubmission(group);
                      const isSubmitted = Boolean(groupSubmission);
                      const isReviewed = groupSubmission ? isSubmissionReviewed(groupSubmission) : false;
                      return (
                        <div
                          key={group.id}
                          role="button"
                          tabIndex={0}
                          className="cursor-pointer rounded-lg border bg-background p-4 transition-colors hover:bg-accent/20"
                          onClick={() => {
                            setSelectedTeamGroupId(group.id);
                            setIsTeamAnswerFormOpen(false);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedTeamGroupId(group.id);
                              setIsTeamAnswerFormOpen(false);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-xl font-semibold">{group.group_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatSimpleDate(group.created_date)}
                              </p>
                              {groupSubmission ? (
                                <p className="text-xs text-muted-foreground">
                                  Submitted: {formatAssignmentDeadline(groupSubmission.submission_date)}
                                </p>
                              ) : null}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <EllipsisVertical className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  disabled={isLeavingStudentGroup}
                                  onClick={() => void handleLeaveTeam(group.id)}
                                >
                                  Leave team
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={isDeletingStudentGroup}
                                  onClick={() => setStudentGroupToDelete(group.id)}
                                >
                                  Delete team
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <div className="mt-3">
                            <span
                              className={
                                isSubmitted
                                  ? isReviewed
                                    ? 'rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300'
                                    : 'rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300'
                                  : 'rounded-md border border-muted-foreground/20 bg-muted px-2 py-0.5 text-xs text-muted-foreground'
                              }
                            >
                              {isSubmitted ? (isReviewed ? 'Reviewed' : 'Pending') : 'Not submitted'}
                            </span>
                          </div>

                          <div className="mt-4 flex items-center justify-end gap-2 text-sm text-muted-foreground">
                            <span>Members:</span>
                            <div className="flex items-center">
                              {group.users.slice(0, 4).map((user, index) => (
                                <Tooltip key={user.id}>
                                  <TooltipTrigger asChild>
                                    <Avatar
                                      className={`h-7 w-7 border-2 border-background ${index > 0 ? '-ml-2' : ''}`}
                                    >
                                      <AvatarImage src={user.photo_url || undefined} />
                                      <AvatarFallback>{getInitials(user.name, '')}</AvatarFallback>
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent sideOffset={6}>{user.name}</TooltipContent>
                                </Tooltip>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-10 text-center">
                    <Users className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">No teams yet.</p>
                  </div>
                )}
              </TabsContent>
            ) : null}

            <TabsContent value="details" className="mt-3">
              <div className="rounded-lg border bg-background p-4">
                {selectedAssignmentBrief ? (
                  <ReadOnlyContentDisplay html={selectedAssignmentBrief} />
                ) : (
                  <p className="text-sm text-muted-foreground">No details available.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="attachments" className="mt-3">
              {selectedAssignmentAttachments.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {selectedAssignmentAttachments.map((file) => (
                    <a
                      key={file.id}
                      href={file.document || '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border bg-background p-4 hover:bg-accent/20"
                    >
                      <p className="truncate text-sm font-medium">{file.name || 'Attachment'}</p>
                      <p className="mt-1 text-xs text-muted-foreground">Open file</p>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-10 text-center">
                  <FolderOpen className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">No attachments available.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="skills" className="mt-3">
              {selectedAssignmentSkills.length > 0 ? (
                <div className="space-y-4 rounded-lg border bg-background p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="inline-flex rounded-md border bg-muted/50 p-0.5">
                      <button
                        type="button"
                        className={
                          skillView === 'chart'
                            ? 'inline-flex items-center gap-2 rounded-[5px] bg-background px-3 py-1.5 text-sm'
                            : 'inline-flex items-center gap-2 rounded-[5px] px-3 py-1.5 text-sm text-muted-foreground'
                        }
                        onClick={() => setSkillView('chart')}
                      >
                        <BarChart3 className="h-4 w-4" />
                        Chart
                      </button>
                      <button
                        type="button"
                        className={
                          skillView === 'list'
                            ? 'inline-flex items-center gap-2 rounded-[5px] bg-background px-3 py-1.5 text-sm'
                            : 'inline-flex items-center gap-2 rounded-[5px] px-3 py-1.5 text-sm text-muted-foreground'
                        }
                        onClick={() => setSkillView('list')}
                      >
                        <List className="h-4 w-4" />
                        List
                      </button>
                    </div>
                    {skillView === 'chart' ? (
                      <div className="grid w-full grid-cols-1 gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground sm:w-auto sm:grid-cols-3 lg:grid-cols-5">
                        {Object.entries(SKILL_LEVEL_LABELS).map(([level, label]) => (
                          <span key={level} className="inline-flex items-center gap-1.5">
                            <span className="font-semibold">{level}:</span> {label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {skillView === 'list' ? (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                      <div className="space-y-3">
                        {selectedAssignmentSkillsChartData.map((skill) => (
                          <div
                            key={skill.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3"
                          >
                            <p className="text-sm font-medium text-primary">{skill.skill}</p>
                            <div className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                              {skill.level.toFixed(1)} <Star className="h-4 w-4 fill-current" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="h-fit rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        <div className="space-y-3">
                          {Object.entries(SKILL_LEVEL_LABELS).map(([level, label]) => (
                            <div key={level} className="flex items-center gap-1.5">
                              <span className="font-semibold">{level}:</span> {label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="min-w-[640px]">
                        <ChartContainer
                          config={{
                            level: {
                              label: 'Required level',
                              theme: {
                                light: 'hsl(236 86% 69%)',
                                dark: 'hsl(214 95% 72%)',
                              },
                            },
                          }}
                          className="h-[240px] w-full max-w-none sm:h-[280px]"
                        >
                          <BarChart
                            data={selectedAssignmentSkillsChartData}
                            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                            layout="vertical"
                          >
                            <CartesianGrid horizontal={false} />
                            <XAxis type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                            <YAxis
                              type="category"
                              dataKey="skill"
                              width={170}
                              tickLine={false}
                              axisLine={false}
                            />
                            <ChartTooltip
                              cursor={false}
                              content={<ChartTooltipContent indicator="line" />}
                            />
                            <Bar dataKey="level" radius={8} fill="var(--color-level)" barSize={18} />
                          </BarChart>
                        </ChartContainer>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-10 text-center">
                  <Sparkles className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">No skills linked.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      ) : effectiveView === 'groups' && !courseSectionId ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assignmentGroups.map((group) => (
            <button
              key={group.key}
              type="button"
              className="rounded-xl border bg-card p-5 text-left shadow-sm transition-colors hover:bg-accent/30"
              onClick={() => {
                if (group.key === 'course') {
                  setSelectedGroupSectionId(null);
                  setActiveAssignmentView('list');
                  return;
                }
                if (group.sectionId && onNavigateToSectionAssignments) {
                  onNavigateToSectionAssignments(group.sectionId);
                  return;
                }
                if (group.sectionId) {
                  setSelectedGroupSectionId(group.sectionId);
                  setActiveAssignmentView('list');
                }
              }}
            >
              <div className="flex h-full min-h-[120px] flex-col justify-between">
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{group.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {group.count} assignment{group.count === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {!courseSectionId ? (
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              onClick={() => setActiveAssignmentView('groups')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          ) : null}

          <h3 className="text-lg font-semibold">{selectedListLabel}</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {canEdit ? (
              <button
                type="button"
                className="rounded-xl border border-dashed bg-card p-5 text-muted-foreground hover:border-primary/50"
                onClick={() => {
                  if (!tenant) return;
                  const params = new URLSearchParams();
                  if (effectiveSelectedGroupSectionId) {
                    params.set('section', String(effectiveSelectedGroupSectionId));
                  }
                  const query = params.toString();
                  navigate(
                    `/${tenant}/courses/${courseId}/add-assignment${query ? `?${query}` : ''}`
                  );
                }}
              >
                <div className="flex min-h-[118px] flex-col items-center justify-center gap-2">
                  <Plus className="h-5 w-5" />
                  <span className="font-medium">{addLabel}</span>
                </div>
              </button>
            ) : null}

            {displayedAssignments.map((assignment) => (
              <div
                key={assignment.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer rounded-xl border bg-card text-left shadow-sm transition-colors hover:bg-accent/20"
                onClick={() => {
                  setSelectedAssignmentId(assignment.id);
                  if (assignment.assignment_type === 'GROUP') {
                    setAssignmentDetailTab(canEdit ? 'submissions' : 'teams');
                  } else {
                    setAssignmentDetailTab(canEdit ? 'submissions' : 'details');
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedAssignmentId(assignment.id);
                    if (assignment.assignment_type === 'GROUP') {
                      setAssignmentDetailTab(canEdit ? 'submissions' : 'teams');
                    } else {
                      setAssignmentDetailTab(canEdit ? 'submissions' : 'details');
                    }
                  }
                }}
              >
                <div className="border-b p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="line-clamp-1 text-base font-semibold">{assignment.assignment_name}</p>
                    {canEdit ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EllipsisVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingAssignment(assignment);
                              setIsDrawerOpen(true);
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignmentToDelete(assignment);
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                  <div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock3
                      className={
                        isPastAssignmentDeadline(assignment.submission_deadline)
                          ? 'h-3.5 w-3.5 text-red-500'
                          : 'h-3.5 w-3.5 text-muted-foreground'
                      }
                    />
                    <span
                      className={
                        isPastAssignmentDeadline(assignment.submission_deadline)
                          ? 'text-red-500'
                          : 'text-muted-foreground'
                      }
                    >
                      {formatAssignmentDeadline(assignment.submission_deadline)}
                    </span>
                  </div>
                  <div className="mt-3">
                    <span
                      className={
                        assignment.assignment_type === 'GROUP'
                          ? 'inline-flex rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300'
                          : 'inline-flex rounded-md border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300'
                      }
                    >
                      {assignment.assignment_type === 'GROUP'
                        ? 'Team assignment'
                        : 'Individual assignment'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 p-3 text-xs text-muted-foreground">
                  <div>
                    Full marks:{' '}
                    <span className="text-foreground">{assignment.marking_scheme?.full_marks ?? 0}</span>
                  </div>
                  <div className="text-right">
                    Weightage:{' '}
                    <span className="text-foreground">
                      {assignment.marking_scheme?.course_percentage ?? 0}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {displayedAssignments.length === 0 && !canEdit ? (
            <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
              {emptyText}
            </div>
          ) : null}
        </div>
      )}

      <CourseAssignmentSubmissionSheet
        open={Boolean(selectedSubmissionId) && !isGroupAssignment}
        canEdit={canEdit}
        hasAIModule={hasAIModule}
        isAIFeedbackLoading={isFetchingAIFeedback}
        selectedSubmission={selectedSubmission}
        selectedSubmissionOwnFeedback={selectedSubmissionOwnFeedback}
        selectedAssignmentDeadline={selectedAssignmentDeadline}
        isSubmissionReviewed={isSubmissionReviewed}
        isLateSubmission={isLateSubmission}
        formatAssignmentDeadline={formatAssignmentDeadline}
        getInitials={getInitials}
        onOpenChange={(open) => {
          if (!open) setSelectedSubmissionId(null);
        }}
        onClose={() => setSelectedSubmissionId(null)}
        onOpenGradeDialog={(submission) => handleOpenGradeDialog(submission)}
        onOpenAIFeedback={(submission) => void handleOpenAIFeedback(submission)}
      />

      <Sheet
        open={Boolean(selectedTeamGroupId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTeamGroupId(null);
            setActiveMemberPopoverKey(null);
            setIsTeamAnswerFormOpen(false);
            setTeamAnswerDraft('');
            setTeamAnswerAttachment(null);
            teamAnswerGetterRef.current = null;
            setIsReflectiveDrawerOpen(false);
            setReflectiveDraft('');
            setReflectiveAttachment(null);
            reflectiveGetterRef.current = null;
            setSelectedReflectiveSubmissionId(null);
          }
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-[min(97vw,1360px)]" showClose={false}>
          {selectedTeamGroup ? (
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                    onClick={() => {
                      setSelectedTeamGroupId(null);
                      setIsTeamAnswerFormOpen(false);
                      setTeamAnswerDraft('');
                      setTeamAnswerAttachment(null);
                      teamAnswerGetterRef.current = null;
                      setIsReflectiveDrawerOpen(false);
                      setReflectiveDraft('');
                      setReflectiveAttachment(null);
                      reflectiveGetterRef.current = null;
                      setSelectedReflectiveSubmissionId(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                  <p className="text-lg font-semibold">{selectedTeamGroup.group_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-primary"
                    onClick={() => openEditTeamDialog(selectedTeamGroup.id)}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setStudentGroupToDelete(selectedTeamGroup.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>

              <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px]">
                <div className="min-h-0 overflow-y-auto border-r p-4">
                  {selectedTeamSubmission && !isTeamAnswerFormOpen ? (
                    <div className="space-y-4">
                      <div className="rounded-lg border bg-background p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Submission date</p>
                            <p className="text-sm font-semibold">
                              {formatAssignmentDeadline(selectedTeamSubmission.submission_date)}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {isSubmissionReviewed(selectedTeamSubmission) ? (
                              <span className="rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300">
                                Reviewed
                              </span>
                            ) : (
                              <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                                Pending
                              </span>
                            )}
                            {isLateSubmission(
                              selectedTeamSubmission.submission_date,
                              selectedAssignmentDeadline
                            ) ? (
                              <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                                Late submission
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 rounded-lg border bg-background p-4">
                        <h4 className="text-sm font-semibold">Submission details</h4>
                        {selectedTeamSubmission.assignment_details ? (
                          <ReadOnlyContentDisplay
                            html={selectedTeamSubmission.assignment_details}
                            className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">No details submitted.</p>
                        )}
                      </div>

                      <div className="space-y-2 rounded-lg border bg-background p-4">
                        <h4 className="text-sm font-semibold">Attachment</h4>
                        {selectedTeamSubmission.assignment_file ? (
                          <a
                            href={selectedTeamSubmission.assignment_file}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            View submission file
                          </a>
                        ) : (
                          <p className="text-sm text-muted-foreground">No attachment uploaded.</p>
                        )}
                      </div>

                      <div className="space-y-3 rounded-lg border bg-background p-4">
                        <h4 className="text-sm font-semibold">Feedback</h4>
                        {selectedTeamSubmission.student_submission.length > 0 ? (
                          <div className="space-y-3">
                            {selectedTeamSubmission.student_submission.map((feedbackItem) => (
                              <div key={feedbackItem.id} className="rounded-md border bg-muted/20 p-3">
                                <div className="mb-2 flex items-center gap-2">
                                  <Avatar className="h-7 w-7">
                                    <AvatarImage
                                      src={feedbackItem.feedback_provided_by.photo_url || undefined}
                                    />
                                    <AvatarFallback>
                                      {getInitials(
                                        feedbackItem.feedback_provided_by.first_name,
                                        feedbackItem.feedback_provided_by.last_name
                                      )}
                                    </AvatarFallback>
                                  </Avatar>
                                  <p className="text-sm font-medium">
                                    {feedbackItem.feedback_provided_by.first_name}{' '}
                                    {feedbackItem.feedback_provided_by.last_name}
                                  </p>
                                </div>
                                {feedbackItem.feedback ? (
                                  <ReadOnlyContentDisplay
                                    html={feedbackItem.feedback}
                                    className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                                  />
                                ) : (
                                  <p className="text-sm text-muted-foreground">No feedback message.</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No feedback added yet.</p>
                        )}
                      </div>
                    </div>
                  ) : isTeamAnswerFormOpen ? (
                    <div className="flex min-h-full flex-col">
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                        Note: Submission requires either an answer or an attachment.
                      </div>
                      <div className="mt-4 space-y-1">
                        <label className="text-sm font-medium">Answer</label>
                        <div className="h-[420px] overflow-hidden rounded-xl border bg-background/60 p-1">
                          <RichTextEditor
                            value={teamAnswerDraft}
                            onChange={setTeamAnswerDraft}
                            placeholder="Add answer"
                            className="[&_.editor-container]:m-0 [&_.editor-container]:h-full [&_.editor-inner]:h-full [&_.editor-scroller]:max-h-full [&_.editor-scroller]:overflow-y-auto [&_.editor-input]:min-h-full"
                            minHeight={0}
                            onProvideGetSerializedState={(getSerializedState) => {
                              teamAnswerGetterRef.current = getSerializedState;
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-5 space-y-2">
                        <label className="text-sm font-medium">Attachment</label>
                        <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Drag and drop files here</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Supported files: PDF, PNG, JPEG.
                          </p>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            className="hidden"
                            onChange={(event) => {
                              const file = event.target.files?.[0] || null;
                              setTeamAnswerAttachment(file);
                            }}
                          />
                        </label>
                        {teamAnswerAttachment ? (
                          <div className="rounded-md border bg-background px-3 py-2 text-sm">
                            {teamAnswerAttachment.name}
                          </div>
                        ) : null}
                      </div>
                      <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsTeamAnswerFormOpen(false);
                            setTeamAnswerDraft('');
                            setTeamAnswerAttachment(null);
                            teamAnswerGetterRef.current = null;
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => void handleSubmitTeamAnswer()}
                          disabled={isCreatingStudentSubmission}
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed bg-muted/10 p-6 text-center">
                      <FileQuestion className="h-24 w-24 text-muted-foreground/60" />
                      <p className="text-sm text-muted-foreground">
                        {canEdit ? 'No team data available.' : 'No team submission yet.'}
                      </p>
                      {!canEdit ? (
                        <Button type="button" onClick={() => setIsTeamAnswerFormOpen(true)}>
                          <Plus className="h-4 w-4" />
                          Add team answer
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="min-h-0 overflow-y-auto border-t bg-muted/20 lg:border-t-0">
                  <div className="space-y-4 p-5">
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-muted-foreground">Status</p>
                        {selectedTeamSubmission ? (
                          canEdit ? (
                            selectedTeamSubmissionOwnFeedback ? (
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary transition-colors hover:bg-primary/15"
                                onClick={() =>
                                  handleOpenGradeDialog(
                                    selectedTeamSubmission,
                                    selectedTeamGroup.users.map((member) => member.id),
                                    false
                                  )
                                }
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Edit feedback</span>
                              </button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() =>
                                  handleOpenGradeDialog(
                                    selectedTeamSubmission,
                                    selectedTeamGroup.users.map((member) => member.id),
                                    false
                                  )
                                }
                              >
                                Grade this submission
                              </Button>
                            )
                          ) : null
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {selectedTeamSubmission ? (
                          isSubmissionReviewed(selectedTeamSubmission) ? (
                            <span className="rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300">
                              Reviewed
                            </span>
                          ) : (
                            <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                              Pending
                            </span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">N/A</span>
                        )}
                        {selectedTeamSubmission &&
                        isLateSubmission(selectedTeamSubmission.submission_date, selectedAssignmentDeadline) ? (
                          <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                            Late submission
                          </span>
                        ) : null}
                        {selectedTeamScore !== null ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-0.5 text-xs font-medium text-white">
                            <Trophy className="h-3.5 w-3.5" />
                            {selectedTeamScore} / {selectedAssignmentMarkingScheme?.full_marks ?? 0}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">Submission date</p>
                      <p className="mt-1 text-sm">
                        {selectedTeamSubmission
                          ? formatAssignmentDeadline(selectedTeamSubmission.submission_date)
                          : 'N/A'}
                      </p>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Team leader</p>
                      <div className="mt-2 flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={selectedTeamGroup.group_leader?.photo_url || undefined} />
                          <AvatarFallback>{getInitials(selectedTeamGroup.group_leader?.name, '')}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">{selectedTeamGroup.group_leader?.name}</span>
                        {!canEdit ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Change team leader</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-72 p-2">
                              <p className="mb-2 px-2 text-sm font-medium">Select team leader</p>
                              <div className="space-y-1">
                                {selectedTeamGroup.users.map((member) => (
                                  <button
                                    key={member.id}
                                    type="button"
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted"
                                    onClick={() =>
                                      void handleChangeTeamLeader(selectedTeamGroup.id, member.id)
                                    }
                                  >
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage src={member.photo_url || undefined} />
                                      <AvatarFallback>{getInitials(member.name, '')}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-medium">{member.name}</p>
                                      <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : null}
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">
                        Members <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">{selectedTeamGroup.users.length}</span>
                      </p>
                      <div className="mt-2 flex items-center gap-1.5">
                        {selectedTeamGroup.users.map((member) => {
                          const popoverKey = `${selectedTeamGroup.id}-${member.id}`;
                          return (
                            <Popover
                              key={member.id}
                              open={activeMemberPopoverKey === popoverKey}
                              onOpenChange={(open) => {
                                setActiveMemberPopoverKey(open ? popoverKey : null);
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="rounded-full"
                                  onMouseEnter={() => setActiveMemberPopoverKey(popoverKey)}
                                  onMouseLeave={() => setActiveMemberPopoverKey((prev) => (prev === popoverKey ? null : prev))}
                                  onClick={(event) => event.preventDefault()}
                                >
                                  <Avatar className="h-8 w-8 border">
                                    <AvatarImage src={member.photo_url || undefined} />
                                    <AvatarFallback>{getInitials(member.name, '')}</AvatarFallback>
                                  </Avatar>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-60 p-3"
                                onMouseEnter={() => setActiveMemberPopoverKey(popoverKey)}
                                onMouseLeave={() =>
                                  setActiveMemberPopoverKey((prev) => (prev === popoverKey ? null : prev))
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={member.photo_url || undefined} />
                                    <AvatarFallback>{getInitials(member.name, '')}</AvatarFallback>
                                  </Avatar>
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">{member.name}</p>
                                    <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-destructive hover:underline"
                                  onClick={() =>
                                    void handleRemoveMemberFromGroup(selectedTeamGroup.id, member.id)
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Remove user?
                                </button>
                              </PopoverContent>
                            </Popover>
                          );
                        })}
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border text-muted-foreground hover:bg-muted"
                          onClick={() => openEditTeamDialog(selectedTeamGroup.id)}
                        >
                          <Plus className="h-4 w-4" />
                          <span className="sr-only">Add member</span>
                        </button>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Reflective report</p>
                      {canEdit ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          onClick={() => {
                            setIsReflectiveDrawerOpen(true);
                            setSelectedReflectiveSubmissionId(
                              selectedTeamReflectiveSubmissions[0]?.id || null
                            );
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          View self-reflective reports
                        </button>
                      ) : selectedTeamOwnReflectiveSubmission ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          onClick={() => {
                            setIsReflectiveDrawerOpen(true);
                            setSelectedReflectiveSubmissionId(selectedTeamOwnReflectiveSubmission.id);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          View self-reflective report
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          onClick={() => {
                            setIsReflectiveDrawerOpen(true);
                            setSelectedReflectiveSubmissionId(null);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          Add self-reflective report
                        </button>
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Assignment</p>
                      <p className="mt-1 text-sm font-medium">{selectedAssignmentName}</p>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Due date</p>
                      <p
                        className={`mt-1 inline-flex items-center gap-1 text-sm ${isPastAssignmentDeadline(selectedAssignmentDeadline) ? 'text-red-500' : 'text-foreground'}`}
                      >
                        <Clock3 className="h-4 w-4" />
                        {formatAssignmentDeadline(selectedAssignmentDeadline)}
                      </p>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Team created on</p>
                      <p className="mt-1 text-sm">{formatAssignmentDeadline(selectedTeamGroup.created_date)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet
        open={isReflectiveDrawerOpen}
        onOpenChange={(open) => {
          setIsReflectiveDrawerOpen(open);
          if (!open) {
            setReflectiveDraft('');
            setReflectiveAttachment(null);
            reflectiveGetterRef.current = null;
            setSelectedReflectiveSubmissionId(null);
          }
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-[min(97vw,1280px)]" showClose={false}>
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                onClick={() => setIsReflectiveDrawerOpen(false)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
              <p className="text-lg font-semibold">Reflective reports</p>
            </div>

            {canEdit ? (
              <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[250px_minmax(0,1fr)]">
                <div className="min-h-0 overflow-y-auto border-r p-3">
                  {selectedTeamReflectiveSubmissions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedTeamReflectiveSubmissions.map((submission) => (
                        <button
                          key={submission.id}
                          type="button"
                          className={`w-full rounded-lg border p-3 text-left transition-colors ${
                            selectedReflectiveSubmissionId === submission.id
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-accent/30'
                          }`}
                          onClick={() => setSelectedReflectiveSubmissionId(submission.id)}
                        >
                          <div className="flex items-center gap-2">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={submission.submitted_by.photo_url || undefined} />
                              <AvatarFallback>
                                {getInitials(
                                  submission.submitted_by.first_name,
                                  submission.submitted_by.last_name
                                )}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">
                                {submission.submitted_by.first_name} {submission.submitted_by.last_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {isSubmissionReviewed(submission) ? 'Reviewed' : 'Pending'}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-4 text-center">
                      <FileQuestion className="mx-auto h-8 w-8 text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">No reflective reports yet.</p>
                    </div>
                  )}
                </div>

                <div className="min-h-0 overflow-y-auto p-4">
                  {selectedReflectiveSubmission ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Avatar className="h-9 w-9">
                          <AvatarImage
                            src={selectedReflectiveSubmission.submitted_by.photo_url || undefined}
                          />
                          <AvatarFallback>
                            {getInitials(
                              selectedReflectiveSubmission.submitted_by.first_name,
                              selectedReflectiveSubmission.submitted_by.last_name
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <p className="text-sm font-semibold">
                          {selectedReflectiveSubmission.submitted_by.first_name}{' '}
                          {selectedReflectiveSubmission.submitted_by.last_name}
                        </p>
                        <span className="text-sm text-muted-foreground">
                          {formatAssignmentDeadline(selectedReflectiveSubmission.submission_date)}
                        </span>
                        {isLateSubmission(
                          selectedReflectiveSubmission.submission_date,
                          selectedAssignmentDeadline
                        ) ? (
                          <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                            Late submission
                          </span>
                        ) : null}
                      </div>

                      <div className="space-y-2 rounded-lg border bg-background p-4">
                        <h4 className="text-sm font-semibold">Self reflective report</h4>
                        {selectedReflectiveSubmission.assignment_details ? (
                          <ReadOnlyContentDisplay
                            html={selectedReflectiveSubmission.assignment_details}
                            className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">No details submitted.</p>
                        )}
                      </div>

                      <div className="space-y-2 rounded-lg border bg-background p-4">
                        <h4 className="text-sm font-semibold">Attachments</h4>
                        {selectedReflectiveSubmission.assignment_file ? (
                          <a
                            href={selectedReflectiveSubmission.assignment_file}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            View submission file
                          </a>
                        ) : (
                          <p className="text-sm text-muted-foreground">No attachment uploaded.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-8 text-center">
                      <FileQuestion className="mx-auto h-8 w-8 text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">Select a learner report.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : selectedTeamOwnReflectiveSubmission ? (
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Avatar className="h-9 w-9">
                      <AvatarImage
                        src={selectedTeamOwnReflectiveSubmission.submitted_by.photo_url || undefined}
                      />
                      <AvatarFallback>
                        {getInitials(
                          selectedTeamOwnReflectiveSubmission.submitted_by.first_name,
                          selectedTeamOwnReflectiveSubmission.submitted_by.last_name
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-sm font-semibold">
                      {selectedTeamOwnReflectiveSubmission.submitted_by.first_name}{' '}
                      {selectedTeamOwnReflectiveSubmission.submitted_by.last_name}
                    </p>
                    <span className="text-sm text-muted-foreground">
                      {formatAssignmentDeadline(selectedTeamOwnReflectiveSubmission.submission_date)}
                    </span>
                    {isLateSubmission(
                      selectedTeamOwnReflectiveSubmission.submission_date,
                      selectedAssignmentDeadline
                    ) ? (
                      <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                        Late submission
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-2 rounded-lg border bg-background p-4">
                    <h4 className="text-sm font-semibold">Self reflective report</h4>
                    {selectedTeamOwnReflectiveSubmission.assignment_details ? (
                      <ReadOnlyContentDisplay
                        html={selectedTeamOwnReflectiveSubmission.assignment_details}
                        className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">No details submitted.</p>
                    )}
                  </div>

                  <div className="space-y-2 rounded-lg border bg-background p-4">
                    <h4 className="text-sm font-semibold">Attachments</h4>
                    {selectedTeamOwnReflectiveSubmission.assignment_file ? (
                      <a
                        href={selectedTeamOwnReflectiveSubmission.assignment_file}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        View submission file
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">No attachment uploaded.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-[calc(100vh-64px)] min-h-0 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                    Note: Submission requires either an answer or an attachment.
                  </div>
                  <div className="mt-4 space-y-1">
                    <label className="text-sm font-medium">Answer</label>
                    <div className="h-[420px] overflow-hidden rounded-xl border bg-background/60 p-1">
                      <RichTextEditor
                        value={reflectiveDraft}
                        onChange={setReflectiveDraft}
                        placeholder="Add self reflective report"
                        className="[&_.editor-container]:m-0 [&_.editor-container]:h-full [&_.editor-inner]:h-full [&_.editor-scroller]:max-h-full [&_.editor-scroller]:overflow-y-auto [&_.editor-input]:min-h-full"
                        minHeight={0}
                        onProvideGetSerializedState={(getSerializedState) => {
                          reflectiveGetterRef.current = getSerializedState;
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-5 space-y-2">
                    <label className="text-sm font-medium">Attachment</label>
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                      <p className="text-sm font-medium text-muted-foreground">Drag and drop files here</p>
                      <p className="mt-1 text-xs text-muted-foreground">Supported files: PDF, PNG, JPEG.</p>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setReflectiveAttachment(file);
                        }}
                      />
                    </label>
                    {reflectiveAttachment ? (
                      <div className="rounded-md border bg-background px-3 py-2 text-sm">
                        {reflectiveAttachment.name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 border-t bg-background px-4 py-3">
                  <Button type="button" variant="outline" onClick={() => setIsReflectiveDrawerOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleSubmitReflectiveReport()}
                    disabled={isCreatingStudentSubmission}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <CourseAssignmentTeamDialog
        open={isCreateTeamDialogOpen}
        canEdit={canEdit}
        teamDialogMode={teamDialogMode}
        teamName={teamName}
        selectedTeamMemberOption={selectedTeamMemberOption}
        selectedTeamUserIds={selectedTeamUserIds}
        currentUserId={currentUser?.id}
        filteredTeamUserOptions={filteredTeamUserOptions}
        selectedTeamUsers={selectedTeamUsers}
        isSubmitting={isCreatingStudentGroup || isLeavingStudentGroup}
        onOpenChange={(open) => {
          setIsCreateTeamDialogOpen(open);
          if (!open) {
            setTeamDialogMode('create');
            setTeamDialogGroupId(null);
            setTeamName('');
            setSelectedTeamMemberOption('');
            setSelectedTeamUserIds([]);
          }
        }}
        onTeamNameChange={setTeamName}
        onSelectedTeamMemberOptionChange={setSelectedTeamMemberOption}
        onSelectedTeamUserIdsChange={setSelectedTeamUserIds}
        onRemoveTeamMember={handleRemoveTeamMember}
        onSubmit={() => void handleSubmitCreateTeam()}
      />

      <CourseAssignmentGradeDialog
        open={isGradeDialogOpen}
        isGroupAssignment={isGroupAssignment}
        fullMarks={Number(selectedAssignmentMarkingScheme?.full_marks ?? 0)}
        gradeScoreDraft={gradeScoreDraft}
        gradeFeedbackDraft={gradeFeedbackDraft}
        isSaving={
          isCreatingStudentGrade ||
          isPatchingStudentGrade ||
          isSavingCourseAcquiredMarks ||
          isUpdatingCourseAcquiredMarks
        }
        onOpenChange={(open) => {
          setIsGradeDialogOpen(open);
          if (!open) {
            gradeFeedbackGetterRef.current = null;
            setGradeScoreDraft('');
            setGradeStudentIds([]);
            setActiveGradingSubmission(null);
          }
        }}
        onGradeScoreDraftChange={setGradeScoreDraft}
        onGradeFeedbackDraftChange={setGradeFeedbackDraft}
        onProvideGetSerializedState={(getSerializedState) => {
          gradeFeedbackGetterRef.current = getSerializedState;
        }}
        onCancel={() => setIsGradeDialogOpen(false)}
        onSave={() => void handleSubmitGrade()}
      />

      <CourseAssignmentAnswerSheet
        open={isAnswerDrawerOpen}
        selectedAssignmentName={selectedAssignmentName}
        answerDraft={answerDraft}
        answerAttachment={answerAttachment}
        isSubmitting={isCreatingStudentSubmission}
        onOpenChange={(open) => {
          setIsAnswerDrawerOpen(open);
          if (!open) {
            setAnswerDraft('');
            setAnswerAttachment(null);
            answerGetterRef.current = null;
          }
        }}
        onClose={() => setIsAnswerDrawerOpen(false)}
        onAnswerDraftChange={setAnswerDraft}
        onProvideGetSerializedState={(getSerializedState) => {
          answerGetterRef.current = getSerializedState;
        }}
        onAnswerAttachmentChange={setAnswerAttachment}
        onCancel={() => setIsAnswerDrawerOpen(false)}
        onSubmit={() => void handleSubmitStudentAnswer()}
      />

      <CourseAssignmentCreateDrawer
        key={editingAssignment ? `edit-${editingAssignment.id}` : 'create'}
        tenant={tenant}
        courseId={courseId}
        courseStructureId={courseStructureId}
        courseSectionId={effectiveSelectedGroupSectionId}
        assignmentToEdit={editingAssignment}
        open={isDrawerOpen}
        onOpenChange={(nextOpen) => {
          setIsDrawerOpen(nextOpen);
          if (!nextOpen) setEditingAssignment(null);
        }}
      />

      <CourseAssignmentSkillsAssessmentSheet
        open={isSkillAssessmentOpen}
        onOpenChange={setIsSkillAssessmentOpen}
        evaluationUsers={skillAssessmentUsers}
        listingUsers={skillAssessmentListingUsers}
        skills={skillAssessmentSkills}
        evaluationAssignmentId={selectedAssignmentId}
        evaluationCourseId={courseId}
        courseSectionId={courseSectionId ?? null}
        tenant={tenant}
        currentUserId={currentUser?.id ?? null}
      />

      <AlertDialog
        open={Boolean(assignmentToDelete)}
        onOpenChange={(open) => {
          if (!open) setAssignmentToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The assignment will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingAssignment}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingAssignment}
              onClick={async (e) => {
                e.preventDefault();
                if (!tenant || !assignmentToDelete) return;
                try {
                  await deleteAssignment({
                    tenant,
                    assignmentId: assignmentToDelete.id,
                  }).unwrap();
                  toast.success('Assignment deleted successfully.');
                  setAssignmentToDelete(null);
                } catch {
                  toast.error('Failed to delete assignment.');
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(studentGroupToDelete)}
        onOpenChange={(open) => {
          if (!open) setStudentGroupToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete team?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The team will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingStudentGroup}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingStudentGroup}
              onClick={async (event) => {
                event.preventDefault();
                await handleDeleteTeam();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(leaderBlockedLeaveGroup)}
        onOpenChange={(open) => {
          if (!open) setLeaderBlockedLeaveGroup(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unable to leave team</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Assign team leader to other before leaving.
          </p>
          <div className="flex justify-end">
            <Button type="button" onClick={() => setLeaderBlockedLeaveGroup(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseAssignmentsContent;
